import React, { useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { Divider } from '../../../../components/atoms/Divider';
import { PriceTag } from '../../../../components/atoms/PriceTag';
import { SectionItem } from '../../../../components/atoms/SectionItem';
import { TextField } from '../../../../components/atoms/TextField';
import { Typography } from '../../../../components/atoms/Typography';
import { Cart as CartIcon } from '../../../../components/icons/Cart';
import { SectionGroup } from '../../../../components/molecules/SectionGroup';
import { Basket } from '../../../../components/organisms/Basket';
import { Menu } from '../../../../core/api/menu/menu.types';
import {
  Campaign,
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
  PickupLocationType,
} from '../../../../core/api/restaurant/restaurant.types';
import { AppConfig } from '../../../../core/api/whitelabel/whitelabel.types';
import { useCrossSellMenuItems } from '../../../../core/hooks/fetch/useCrossSellMenuItems';
import { getItemAddedToBasketData } from '../../../../core/services/analyticsService/analyticsUtils';
import { flagService } from '../../../../core/services/flagService';
import {
  BasketActionsType,
  QuantityControlType,
} from '../../../../core/types/basket.types';
import { Routes } from '../../../../core/types/route.types';
import { changeUtils } from '../../../../utils/changeUtils';
import { getTaxLabel } from '../../../../utils/localisationUtils';
import { Track } from '../../../../weborder/analytics';
import * as Api from '../../../../weborder/Api';
import * as BasketControl from '../../../../weborder/BasketControl';
import * as Data from '../../../../weborder/Data';
import { OrderPayload } from '../../../../weborder/selectors/basketSelectors';
import { ChefNotes } from '../../../../weborder/Store';
import { CrossSellList } from '../../components/CrossSellList';
import { LoyaltyCard } from '../../components/LoyaltyCard';
import { TipEmoji } from '../../components/Tipping/TipEmoji';
import { Tipping } from '../../components/Tipping/Tipping';
import { TippingValue } from '../../components/Tipping/TippingOption';
import { checkoutUtils } from '../../utils';
import { RoundUpTip } from '../RoundUpTip';
import { ServiceChargeSetting } from './partials/ServiceChargeSetting';
import { VoucherSetting } from './partials/VoucherSetting';

type EnhancedCustomer = Data.Async<Api.Customer>;

type Props = {
  account: Api.PaymentAccount;
  addItemToBasket: QuantityControlType['onAddItem'];
  appConfig: AppConfig;
  basket: BasketControl.Data;
  chefNotes: ChefNotes;
  currentLang: string;
  customer: EnhancedCustomer;
  editChefNotes: (vrid: number, newValue: string) => void;
  isRoundUpEnabled: boolean;
  loyaltyCampaign?: Campaign;
  menu: Menu;
  orderItemTotal: number;
  orderPayload: OrderPayload;
  orderRoundUpAmount: number;
  orderTipsTotal: number;
  orderTotal: number;
  orderTotalToPay: number;
  params: Routes['params'];
  percentageTip: number;
  pickupLocationType: PickupLocationType;
  removeItemFromBasket: QuantityControlType['onRemoveItem'];
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail;
  setRoundUpEnabled: (isRoundUpEnabled: boolean) => void;
  setTip: (tip: number) => void;
  tableNumber: number;
  toggleExcludeServiceCharge: () => void;
  toggleVoucherDialog: () => void;
  translate: any;
  unavailableBasketItems?: string[];
};

const useStyles = makeStyles((theme: Theme) => ({
  basket: {
    position: 'static',
    width: '100%',

    '& $basketItemsCheckout': {
      display: 'block',
    },

    '& ul': {
      margin: 0,
    },

    '& > div': {
      borderLeft: 'inherit',
      borderRight: 'inherit',
      borderTop: 'none',
    },
  },
  basketItemsCheckout: {
    display: 'block',
    padding: 0,
    width: '100%',

    '& .concessionBrand:first-of-type': {
      marginTop: 0,
    },

    '& > li > div:first-child > h3': {
      marginBottom: theme.spacing(1),
    },

    '& > li > div:last-child > div:first-child': {
      minHeight: 0,
      textAlign: 'right',

      '& > div': {
        marginBottom: theme.spacing(1),
        width: '100%',
      },
    },
  },
  chefNotes: {
    marginTop: theme.spacing(4.5),
  },
  chefNotesWrapper: {
    [theme.breakpoints.up('md')]: {
      paddingBottom: 0,
    },
  },
  loyaltyCardContainer: {
    marginTop: theme.spacing(4.25),
    maxWidth: '328px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  priceTag: {
    minHeight: 0,
  },
  priceTagTotal: {
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(24),

      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(21),
      },
    },
  },
  procesingFeeInfo: {
    paddingTop: theme.spacing(1),
  },
  roundUp: {
    padding: theme.spacing(2, 0),
  },
  tipping: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  tipTotal: {
    display: 'flex',
    flexFlow: 'row nowrap',
  },
}));

export function BasketSection(props: Props): JSX.Element {
  const classes = useStyles();

  const {
    account,
    addItemToBasket,
    appConfig,
    basket,
    chefNotes,
    currentLang,
    customer,
    editChefNotes,
    loyaltyCampaign,
    menu,
    orderItemTotal,
    orderPayload,
    orderRoundUpAmount,
    orderTipsTotal,
    orderTotal,
    orderTotalToPay,
    params,
    percentageTip,
    pickupLocationType,
    removeItemFromBasket,
    restaurant,
    isRoundUpEnabled,
    setTip,
    setRoundUpEnabled,
    tableNumber,
    toggleExcludeServiceCharge,
    toggleVoucherDialog,
    translate,
    unavailableBasketItems = [],
  } = props;

  const vrId = Number(params.vrid);

  const showRoundUpFlagValue = flagService.getFlagValue('showRoundUp');
  const isLoyaltyStatusEnabled = flagService.isFlagOn('loyaltyStatus');
  const isCrossSellEnabled = flagService.isFlagOn('crossSell');

  const {
    data: crossSellMenuItemsData,
    isLoading: isCrossSellMenuItemsLoading,
    isError: isCrossSellMenuItemsError,
  } = useCrossSellMenuItems(basket?.items, menu, orderTotal);

  const { Currency: currency } = restaurant;

  const [chefNote, setChefNote] = useState({ type: 'loaded' });
  const [hasCustomTip, setHasCustomTip] = useState<boolean>(false);

  if (customer.status !== 'success') {
    return null;
  }

  const { Category } = appConfig;

  const getLocalisedTotal: (numberTotal: number) => string = (
    numberTotal: number,
  ) => BasketControl.ToCurrency(numberTotal, currency, currentLang);

  const actions: BasketActionsType = {
    onAddItem: (basketItem) => {
      const itemType = basketItem.Options.length === 0 ? 'simple' : 'complex';

      const eventData = getItemAddedToBasketData(
        basketItem.Item,
        itemType,
        vrId,
        'Checkout',
        'Add Item To Basket Via Checkout',
        'Orders',
      );

      Track(
        'Orders',
        'Add Item To Basket Via Checkout',
        eventData,
        'AddToCart',
      );
      addItemToBasket(basketItem);
    },
    onCheckout: () => {},
    onRemoveItem: (basketItem) => {
      const { menuId, index, indexName } = basketItem;

      const eventData = {
        amp_itemName: indexName,
        amp_itemPrice: 0,
        amp_itemRemovedFrom: 'Checkout',
        amp_label: 'Item Removed from Basket',
        eventLabel: basketItem.indexName,
        label: 'Remove Item From Basket Via Checkout',
        menuId,
        section: 'Orders',
        virtualRestaurantId: vrId,
      };
      Track('Orders', 'Remove Item From Basket Via Checkout', eventData);
      removeItemFromBasket({ menuId, indexName, index });
    },
  };

  const categoryNoteTranslation =
    checkoutUtils.getCategoryNoteTranslation(Category);

  const notesForTheChefText = `${translate('Notes_for_the_chef', {
    categoryNote: translate(categoryNoteTranslation),
  })} (${translate('Optional')})`;

  const notesForTheChefValue =
    checkoutUtils.getChefNote(chefNote, chefNotes, restaurant) || ' ';

  const handleChefNoteChange = (event: React.FormEvent<any>): void => {
    const newValue: string = (event.target as any).value;
    setChefNote({
      type: 'custom',
    });

    editChefNotes(vrId, newValue);
  };

  const handleChefNoteBlur = (event: React.FormEvent<any>): void => {
    const newValue: string = (event.target as any).value;
    if (newValue && newValue.length > 0) {
      const chefNotesData = {
        amp_label: 'Chef Note Added',
        amp_numberOfCharacters: newValue.length,
      };
      Track('Orders', 'Chef Note Added', chefNotesData);
    }
  };

  const handleHasCustomTipChange = (hasTip: boolean): void => {
    setHasCustomTip(hasTip);
  };

  const createTippingOptionObjects = (
    tippingValues: number[],
  ): TippingValue[] => {
    const newTippingOptions: TippingValue[] = [];

    tippingValues.forEach((value) => {
      const monetaryValue = changeUtils.round(orderTotal * (value / 100));

      newTippingOptions.push({
        subValue: getLocalisedTotal(monetaryValue),
        value: `${value}%`,
      });
    });

    return newTippingOptions;
  };

  const processingFee = checkoutUtils.computePurchaseFee(
    account,
    basket,
    pickupLocationType,
    restaurant,
  );

  const isTipEnabled = checkoutUtils.isTipEnabled(
    appConfig,
    customer,
    restaurant,
  );

  const hasTipablePaymentMethod = checkoutUtils.isTipablePaymentMethod(account);
  const tippingOptions = checkoutUtils.getTippingRates(restaurant);
  const areEmojisEnabled = checkoutUtils.areEmojisEnabled(restaurant);
  const isCustomTipEnabled = checkoutUtils.isCustomTipEnabled(restaurant);

  const basketTaxes = checkoutUtils.computeBasketTaxes(basket);
  const taxes = (restaurant.DeliveryFeeTaxAmount || 0) + basketTaxes;

  const deliveryFee: number = checkoutUtils.computeDeliveryFee(restaurant);

  const showCrossSellList = isCrossSellEnabled && !isCrossSellMenuItemsError;
  const showServiceCharge = restaurant.ServiceChargeConfiguration?.IsEnabled;
  const showProcessingFee =
    processingFee > 0 && checkoutUtils.showProcessingFee(restaurant);
  const showTipping = isTipEnabled && hasTipablePaymentMethod;
  const showDeliveryFee = deliveryFee > 0;
  const showTax = restaurant.DisplayTax;
  const showRoundUp =
    showRoundUpFlagValue === 'A' &&
    restaurant.TipsConfig?.AllowRoundUp &&
    showTipping;
  const showAlternativeRoundUp =
    showRoundUpFlagValue === 'B' &&
    restaurant.TipsConfig?.AllowRoundUp &&
    showTipping;

  const orderTotalToPayWithoutTips = orderTotalToPay - orderTipsTotal;

  const roundUpAmountWithoutTip = getLocalisedTotal(
    Math.ceil(orderTotalToPayWithoutTips) - orderTotalToPayWithoutTips,
  );

  const getCustomTipAmount = (): string => {
    if (isRoundUpEnabled && showRoundUp) {
      const orderTipsTotalWithoutRoundUp = orderTipsTotal - orderRoundUpAmount;
      return getLocalisedTotal(orderTipsTotalWithoutRoundUp);
    }
    return getLocalisedTotal(orderTipsTotal);
  };

  return (
    <SectionGroup
      icon={<CartIcon />}
      sectionName="checkout-details"
      title={translate('Checkout_yourOrder').toString()}
    >
      <Divider />

      <Basket
        actions={actions}
        basketItemClassName={classes.basketItemsCheckout}
        basketItems={basket.items}
        buttonLabel=""
        className={classes.basket}
        currency={currency}
        isCheckoutEnabled={false}
        languageCode={currentLang}
        menu={menu}
        minOrder={restaurant.MinimumOrder}
        showBasketTitle={false}
        showCheckoutButton={false}
        translate={translate}
        unavailableBasketItems={unavailableBasketItems}
      />

      {showCrossSellList && (
        <CrossSellList
          addItemToBasket={addItemToBasket}
          crossSellMenuItems={crossSellMenuItemsData}
          currency={currency}
          isLoading={isCrossSellMenuItemsLoading}
          languageCode={currentLang}
          translate={translate}
        />
      )}

      {!showCrossSellList && <Divider />}

      <VoucherSetting
        basket={basket}
        currency={currency}
        currentLang={currentLang}
        orderPayload={orderPayload}
        params={params}
        pickupLocationType={pickupLocationType}
        restaurant={restaurant}
        tableNumber={tableNumber}
        toggleVoucherDialog={toggleVoucherDialog}
        translate={translate}
      />

      {showTipping && (
        <SectionItem
          label={translate('Tip')}
          rightHeader={
            <div className={classes.tipTotal}>
              {areEmojisEnabled && (
                <TipEmoji
                  hasCustomTip={hasCustomTip}
                  percentageTip={percentageTip}
                  tippingOptions={tippingOptions}
                />
              )}
              <PriceTag
                className={classes.priceTag}
                hasMenuOptions={false}
                localisedPrice={getLocalisedTotal(orderTipsTotal)}
                translate={translate}
              />
            </div>
          }
        >
          <div className={classes.tipping}>
            <Tipping
              isCustomTipEnabled={isCustomTipEnabled}
              currency={currency}
              customTipAmountLabel={getCustomTipAmount()}
              hasCustomTip={hasCustomTip}
              isRoundUpEnabled={isRoundUpEnabled && showAlternativeRoundUp}
              onChange={setTip}
              onHasCustomTipChange={handleHasCustomTipChange}
              orderTotal={orderTotal}
              roundUpAmount={roundUpAmountWithoutTip}
              selectedTipValue={percentageTip}
              showRoundUp={showAlternativeRoundUp}
              tippingOptions={createTippingOptionObjects(tippingOptions)}
              toggleRoundUp={() => {
                setRoundUpEnabled(!isRoundUpEnabled);
              }}
              translate={translate}
            />
          </div>
        </SectionItem>
      )}

      {showServiceCharge && (
        <>
          <Divider />

          <ServiceChargeSetting
            basket={basket}
            currency={currency}
            currentLang={currentLang}
            processingFee={processingFee}
            toggleExcludeServiceCharge={toggleExcludeServiceCharge}
            translate={translate}
            restaurant={restaurant}
          />
        </>
      )}

      <SectionItem
        label={translate('Checkout_SubTotal')}
        rightHeader={
          <PriceTag
            className={classes.priceTag}
            hasMenuOptions={false}
            localisedPrice={getLocalisedTotal(orderItemTotal)}
            translate={translate}
          />
        }
      />

      {showProcessingFee && (
        <SectionItem
          label={translate('Online_ordering_fee')}
          rightHeader={
            <PriceTag
              className={classes.priceTag}
              hasMenuOptions={false}
              localisedPrice={getLocalisedTotal(processingFee)}
              translate={translate}
            />
          }
        >
          {showTax && (
            <Typography
              className={classes.procesingFeeInfo}
              variant="body2-secondary"
            >
              (
              {translate('Includes_tax_of', {
                tax: `${restaurant.ProcessingFeeTaxRate}%`,
              })}
              )
            </Typography>
          )}
        </SectionItem>
      )}

      {showDeliveryFee && (
        <SectionItem
          label={translate('Delivery_fee')}
          rightHeader={
            <PriceTag
              className={classes.priceTag}
              hasMenuOptions={false}
              localisedPrice={getLocalisedTotal(deliveryFee)}
              translate={translate}
            />
          }
        />
      )}

      {showTax && (
        <SectionItem
          label={getTaxLabel(appConfig, translate)}
          rightHeader={
            <PriceTag
              className={classes.priceTag}
              hasMenuOptions={false}
              localisedPrice={getLocalisedTotal(taxes)}
              translate={translate}
            />
          }
        />
      )}

      {showRoundUp && (
        <>
          <Divider />

          <div className={classes.roundUp}>
            <RoundUpTip
              amount={getLocalisedTotal(orderRoundUpAmount)}
              isEnabled={isRoundUpEnabled}
              restaurantName={restaurant.RestaurantName}
              toggleRoundUp={setRoundUpEnabled}
              translate={translate}
            />
          </div>
        </>
      )}

      <SectionItem
        className={classes.chefNotesWrapper}
        label={translate('Total')}
        dense={showRoundUp}
        rightHeader={
          <PriceTag
            className={classes.priceTagTotal}
            hasMenuOptions={false}
            localisedPrice={getLocalisedTotal(orderTotalToPay)}
            translate={translate}
          />
        }
      >
        {restaurant.AllowChefNotes && (
          <TextField
            className={classes.chefNotes}
            data-fd="checkout-notes"
            fullWidth
            id="notesForChef"
            label={notesForTheChefText}
            multiline
            onBlur={handleChefNoteBlur}
            onChange={handleChefNoteChange}
            placeholder={translate('Checkout_exampleNote').toString()}
            minRows={2}
            value={notesForTheChefValue}
            variant="standard"
          />
        )}
        {isLoyaltyStatusEnabled && loyaltyCampaign?.RequiredConditions && (
          <div className={classes.loyaltyCardContainer}>
            <LoyaltyCard
              description={loyaltyCampaign.Description}
              requirements={loyaltyCampaign.RequiredConditions[0]}
              showPending
              translate={translate}
            />
          </div>
        )}
      </SectionItem>
    </SectionGroup>
  );
}
